import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";
const ROUGE = "#B85C3A";
const ORANGE = "#D68910";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const FILTERS = [
  { key: "tous", label: "Tous" },
  { key: "os", label: "OS" },
  { key: "mise", label: "Mise en demeure" },
  { key: "note", label: "Note" },
];

function normalizeType(t) {
  return (t || "").toLowerCase();
}

function docMatchesFilter(doc, filterKey) {
  const t = normalizeType(doc.type);
  if (filterKey === "tous") return true;
  if (filterKey === "os") return t.includes("ordre") || t.includes(" os") || t === "os" || t.includes("service");
  if (filterKey === "mise") return t.includes("mise") || t.includes("dem") || t.includes("demeure");
  if (filterKey === "note") return t.includes("note");
  return true;
}

function typeBadgeMeta(type) {
  const t = normalizeType(type);
  if (t.includes("mise") || t.includes("dem")) {
    return { label: "Mise en demeure", bg: "rgba(184,92,58,0.12)", color: ROUGE, border: "1px solid rgba(184,92,58,0.35)" };
  }
  if (t.includes("note")) {
    return { label: "Note interne", bg: "rgba(61,46,32,0.08)", color: TAUPE, border: `1px solid ${BORDER}` };
  }
  if (t.includes("dépôt") || t.includes("depot")) {
    return { label: "Dépôt", bg: "rgba(74,122,155,0.1)", color: "#4A7A9B", border: "1px solid rgba(74,122,155,0.35)" };
  }
  return { label: "Ordre de service", bg: "rgba(192,154,91,0.15)", color: "#7A5F2A", border: "1px solid rgba(192,154,91,0.4)" };
}

export default function EntrepriseDashboardPage({ profile, onLogout }) {
  const [documents, setDocuments] = useState([]);
  const [projetsAssignes, setProjetsAssignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [docFilter, setDocFilter] = useState("tous");
  const [modalDoc, setModalDoc] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadProjectId, setUploadProjectId] = useState("");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const entrepriseId = profile?.id;

  const fetchData = useCallback(async () => {
    if (!entrepriseId) return;
    setLoading(true);
    setLoadError("");
    try {
      const { data: docs, error: e1 } = await supabase
        .from("entreprise_documents")
        .select("*, project:project_id(name)")
        .eq("entreprise_id", entrepriseId)
        .order("created_at", { ascending: false });

      if (e1) throw e1;

      const { data: links, error: e2 } = await supabase
        .from("entreprise_projets")
        .select("project_id, project:project_id(id, name)")
        .eq("entreprise_id", entrepriseId);

      if (e2) throw e2;

      setDocuments(docs || []);

      const seen = new Map();
      (links || []).forEach((row) => {
        const p = row.project;
        if (p?.id && !seen.has(p.id)) seen.set(p.id, p);
      });
      setProjetsAssignes(Array.from(seen.values()));

      const { count, error: e3 } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("destinataire_id", entrepriseId)
        .eq("lu", false);

      if (!e3) setUnreadMsgCount(count ?? 0);
      else setUnreadMsgCount(0);
    } catch (err) {
      console.error(err);
      setLoadError(err.message || "Impossible de charger les données.");
      setDocuments([]);
      setProjetsAssignes([]);
    } finally {
      setLoading(false);
    }
  }, [entrepriseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredDocs = useMemo(
    () => documents.filter((d) => docMatchesFilter(d, docFilter)),
    [documents, docFilter]
  );

  const kpiTotal = documents.length;
  const kpiNonLus = documents.filter((d) => !d.lu).length;
  const kpiEnAttente = documents.filter((d) => !d.reponse && (d.statut === "envoyé" || !d.statut)).length;

  async function markDocLu(docId) {
    await supabase.from("entreprise_documents").update({ lu: true }).eq("id", docId);
    setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, lu: true } : d)));
  }

  function openModal(doc) {
    setModalDoc(doc);
    setReplyText(doc.reponse || "");
    markDocLu(doc.id);
  }

  function closeModal() {
    setModalDoc(null);
    setReplyText("");
  }

  async function sendReply() {
    if (!modalDoc || !replyText.trim()) return;
    setSendingReply(true);
    try {
      const { error } = await supabase
        .from("entreprise_documents")
        .update({
          lu: true,
          reponse: replyText.trim(),
          repondu_at: new Date().toISOString(),
          statut: "répondu",
        })
        .eq("id", modalDoc.id);

      if (error) throw error;

      setDocuments((prev) =>
        prev.map((d) =>
          d.id === modalDoc.id
            ? { ...d, lu: true, reponse: replyText.trim(), repondu_at: new Date().toISOString(), statut: "répondu" }
            : d
        )
      );
      closeModal();
    } catch (err) {
      alert("Erreur : " + (err.message || String(err)));
    } finally {
      setSendingReply(false);
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    if (typeof onLogout === "function") onLogout();
    else window.location.reload();
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!entrepriseId || !uploadProjectId) {
      setUploadMsg("Choisissez un projet.");
      return;
    }
    setUploadBusy(true);
    setUploadMsg("");
    try {
      let fileUrl = "";
      if (uploadFile) {
        const path = `${entrepriseId}/${Date.now()}_${uploadFile.name.replace(/[^\w.-]/g, "_")}`;
        const { error: upErr } = await supabase.storage.from("entreprise-depots").upload(path, uploadFile, {
          upsert: false,
        });
        if (upErr) {
          fileUrl = "";
          setUploadMsg(
            "Stockage non configuré ou refusé — envoi du descriptif uniquement. (" + upErr.message + ")"
          );
        } else {
          const { data: pub } = supabase.storage.from("entreprise-depots").getPublicUrl(path);
          fileUrl = pub?.publicUrl || "";
        }
      }

      const titre = uploadTitle.trim() || (uploadFile ? uploadFile.name : "Dépôt entreprise");
      const contenu =
        (uploadDesc.trim() || "—") + (fileUrl ? `\n\nFichier : ${fileUrl}` : uploadFile ? `\n\nFichier : ${uploadFile.name} (non hébergé)` : "");

      const { error } = await supabase.from("entreprise_documents").insert({
        entreprise_id: entrepriseId,
        project_id: uploadProjectId,
        type: "Dépôt entreprise",
        titre,
        contenu,
        statut: "envoyé",
        lu: false,
      });

      if (error) throw error;

      setUploadFile(null);
      setUploadDesc("");
      setUploadTitle("");
      setUploadMsg("Envoyé à YOMA.");
      fetchData();
    } catch (err) {
      setUploadMsg("Erreur : " + (err.message || String(err)));
    } finally {
      setUploadBusy(false);
    }
  }

  const specialite =
    profile?.specialite ||
    profile?.metadata?.specialite ||
    "Corps de métier — partenaire YOMA";

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style>

      <div
        style={{
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
          padding: "24px 20px 48px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          {/* HEADER */}
          <header
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 28,
              paddingBottom: 20,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 18, flex: "1 1 280px" }}>
              <div
                style={{
                  fontFamily: FF_PLAYFAIR,
                  fontSize: 28,
                  fontWeight: 700,
                  color: GOLD,
                  letterSpacing: "0.08em",
                }}
              >
                YOMA
              </div>
              <div>
                <h1 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 700, color: TAUPE }}>
                  {profile?.full_name || profile?.email || "Mon entreprise"}
                </h1>
                <p style={{ margin: "6px 0 0", fontSize: 14, color: MUTED }}>{specialite}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: `1px solid ${BORDER}`,
                background: BG_CARD,
                color: MUTED,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: FF_DM,
              }}
            >
              Déconnexion
            </button>
          </header>

          {loadError && (
            <div
              style={{
                padding: "14px 18px",
                borderRadius: 12,
                background: "rgba(184,92,58,0.1)",
                border: "1px solid rgba(184,92,58,0.3)",
                color: ROUGE,
                marginBottom: 20,
                fontSize: 14,
              }}
            >
              {loadError}
            </div>
          )}

          {/* KPIs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
              marginBottom: 32,
            }}
          >
            <div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 18px rgba(61,46,32,0.06)",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: "0.05em" }}>
                Documents reçus
              </div>
              <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: TAUPE }}>{kpiTotal}</div>
            </div>
            <div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: kpiNonLus > 0 ? `2px solid ${ROUGE}` : `1px solid ${BORDER}`,
                boxShadow: kpiNonLus > 0 ? "0 4px 16px rgba(184,92,58,0.15)" : "0 4px 18px rgba(61,46,32,0.06)",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Non lus</div>
              <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: kpiNonLus > 0 ? ROUGE : TAUPE }}>
                {kpiNonLus}
              </div>
            </div>
            <div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid rgba(214,137,16,0.45)`,
                boxShadow: "0 4px 18px rgba(214,137,16,0.1)",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>En attente de réponse</div>
              <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: ORANGE }}>{kpiEnAttente}</div>
            </div>
          </div>

          {/* DOCUMENTS */}
          <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, margin: "0 0 14px", color: TAUPE }}>
              Mes documents
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setDocFilter(f.key)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    border: docFilter === f.key ? `2px solid ${GOLD}` : `1px solid ${BORDER}`,
                    background: docFilter === f.key ? "rgba(192,154,91,0.12)" : BG_CARD,
                    color: docFilter === f.key ? TAUPE : MUTED,
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {loading ? (
              <p style={{ color: MUTED, fontStyle: "italic" }}>Chargement…</p>
            ) : filteredDocs.length === 0 ? (
              <div
                style={{
                  background: BG_CARD,
                  borderRadius: 14,
                  padding: 40,
                  textAlign: "center",
                  border: `1px dashed ${BORDER}`,
                  color: MUTED,
                }}
              >
                Aucun document pour ce filtre.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {filteredDocs.map((doc) => {
                  const badge = typeBadgeMeta(doc.type);
                  return (
                    <article
                      key={doc.id}
                      style={{
                        background: BG_CARD,
                        borderRadius: 14,
                        padding: "18px 20px",
                        border: `1px solid ${BORDER}`,
                        boxShadow: "0 4px 16px rgba(61,46,32,0.05)",
                      }}
                    >
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 12, justifyContent: "space-between" }}>
                        <div style={{ flex: "1 1 240px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: badge.bg,
                              color: badge.color,
                              border: badge.border,
                              marginBottom: 8,
                            }}
                          >
                            {badge.label}
                          </span>
                          <h3 style={{ margin: "0 0 6px", fontFamily: FF_PLAYFAIR, fontSize: 16, fontWeight: 700, color: TAUPE }}>
                            {doc.titre}
                          </h3>
                          <p style={{ margin: 0, fontSize: 13, color: MUTED }}>
                            Projet : <strong style={{ color: TAUPE }}>{doc.project?.name || "—"}</strong>
                          </p>
                          <p style={{ margin: "6px 0 0", fontSize: 12, color: MUTED }}>
                            Envoyé le {doc.created_at ? new Date(doc.created_at).toLocaleString("fr-FR") : "—"}
                          </p>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: doc.lu ? "rgba(92,138,92,0.12)" : "rgba(184,92,58,0.1)",
                              color: doc.lu ? "#5C8A5C" : ROUGE,
                            }}
                          >
                            {doc.lu ? "Lu" : "Non lu"}
                          </span>
                          <button
                            type="button"
                            onClick={() => openModal(doc)}
                            style={{
                              padding: "10px 16px",
                              borderRadius: 10,
                              border: "none",
                              background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                              color: "#FFF",
                              fontWeight: 700,
                              fontSize: 13,
                              cursor: "pointer",
                              fontFamily: FF_DM,
                            }}
                          >
                            Voir & Répondre
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* UPLOAD */}
          <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, margin: "0 0 14px", color: TAUPE }}>
              Déposer des documents
            </h2>
            <form
              onSubmit={handleUpload}
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "22px 24px",
                border: `1px solid ${BORDER}`,
                maxWidth: 640,
              }}
            >
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Fichiers (photos / PDF)</label>
              <input
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                style={{ marginBottom: 14, fontSize: 13 }}
              />
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Titre (optionnel)</label>
              <input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Ex. — Photos avancement façade"
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  marginBottom: 14,
                  fontFamily: FF_DM,
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Description</label>
              <textarea
                value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)}
                rows={4}
                placeholder="Décrivez le contenu envoyé à YOMA…"
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  marginBottom: 14,
                  fontFamily: FF_DM,
                  fontSize: 14,
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Projet</label>
              <select
                value={uploadProjectId}
                onChange={(e) => setUploadProjectId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  marginBottom: 14,
                  fontFamily: FF_DM,
                  fontSize: 14,
                  background: BG_CARD,
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                <option value="">— Choisir un projet assigné —</option>
                {projetsAssignes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={uploadBusy}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: uploadBusy ? "wait" : "pointer",
                  fontFamily: FF_DM,
                }}
              >
                {uploadBusy ? "Envoi…" : "Envoyer à YOMA"}
              </button>
              {uploadMsg && <p style={{ marginTop: 12, fontSize: 13, color: MUTED }}>{uploadMsg}</p>}
            </form>
          </section>

          {/* MESSAGERIE */}
          <section>
            <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, margin: "0 0 14px", color: TAUPE }}>
              Messagerie
            </h2>
            <div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "20px 22px",
                border: `1px solid ${BORDER}`,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
                maxWidth: 640,
              }}
            >
              <p style={{ margin: 0, fontSize: 14, color: MUTED, maxWidth: 360 }}>
                Échangez avec l&apos;équipe YOMA depuis la messagerie interne de l&apos;application.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {unreadMsgCount > 0 && (
                  <span
                    style={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: 999,
                      background: ROUGE,
                      color: "#FFF",
                      fontSize: 12,
                      fontWeight: 800,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 8px",
                    }}
                  >
                    {unreadMsgCount > 99 ? "99+" : unreadMsgCount}
                  </span>
                )}
                <span style={{ fontSize: 13, color: MUTED }}>
                  Ouvrez <strong style={{ color: TAUPE }}>Messagerie</strong> dans le menu principal.
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* MODAL */}
      {modalDoc && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(61,46,32,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              maxWidth: 560,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              padding: "24px 26px",
              border: `1px solid ${BORDER}`,
              boxShadow: "0 24px 70px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700, color: TAUPE }}>{modalDoc.titre}</h3>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 22,
                  color: MUTED,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <p style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
              {modalDoc.project?.name ? `Projet : ${modalDoc.project.name}` : ""}
              {modalDoc.created_at && ` · ${new Date(modalDoc.created_at).toLocaleString("fr-FR")}`}
            </p>
            <div
              style={{
                padding: 14,
                borderRadius: 10,
                background: BG,
                border: `1px solid ${BORDER}`,
                fontSize: 14,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                marginBottom: 18,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {modalDoc.contenu || "—"}
            </div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6 }}>Votre réponse</label>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={5}
              placeholder="Saisissez votre réponse…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: `1px solid ${BORDER}`,
                fontFamily: FF_DM,
                fontSize: 14,
                resize: "vertical",
                boxSizing: "border-box",
                marginBottom: 16,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  background: BG,
                  fontFamily: FF_DM,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={sendingReply || !replyText.trim()}
                onClick={sendReply}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontWeight: 700,
                  fontFamily: FF_DM,
                  cursor: sendingReply || !replyText.trim() ? "not-allowed" : "pointer",
                  opacity: !replyText.trim() ? 0.5 : 1,
                }}
              >
                {sendingReply ? "Envoi…" : "Envoyer la réponse"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
